import { Signal, inject } from '@angular/core';

import {
  QueryClient,
  QueryFilters,
  injectMutation,
  injectQuery,
} from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import {
  Dashboard,
  Dimension,
  Feed,
  Workspace,
} from 'src/app/core/models/core.models';
import { augmentObjectWithIconAndImage } from 'src/app/shared/utils/object-utils';
import { UserApiService } from '../services/user-api.service';
import { WorkspaceApiService } from '../services/workspace-api.service';

/**
 * Inputs of the user's workspace list.
 *
 * `withStats` is deliberately NOT part of the query key: it asks the server for a
 * few extra computed fields on the same workspaces, so it is a fetch strategy, not
 * resource identity. One entry per lifecycle status, and turning the stats option
 * on refetches it explicitly (home's `onViewToggle`).
 *
 * `companyId` *is* identity, even though the URL does not carry it: the response is
 * scoped to the session's active company. It is in the key rather than left to
 * `clearQueryCacheOnSessionChange$` alone, because a company switch redirects to
 * `/analytics` - which can resolve to the home route the user is already on, so the
 * component and its observer are never rebuilt. Clearing a cache entry does not
 * make a live observer refetch; changing its key does.
 */
export interface UserWorkspacesScope {
  userId: string;
  companyId: string;
  lifecycleStatus: number;
  withStats: boolean;
}

/** Mirrors GET /user/:id/workspaces. */
export const userWorkspacesQueryKey = ({
  userId,
  companyId,
  lifecycleStatus,
}: UserWorkspacesScope) =>
  ['user', userId, 'workspaces', { companyId, lifecycleStatus }] as const;

/**
 * Every cached workspace list, whatever its lifecycle status. A `predicate` rather
 * than a `['user']` prefix, which would also sweep the activated-companies and
 * recent-object queries.
 */
export const userWorkspacesQueriesFilter = (): QueryFilters => ({
  predicate: (query) => query.queryKey[2] === 'workspaces',
});

const fetchWorkspaces = (userApi: UserApiService, scope: UserWorkspacesScope) =>
  firstValueFrom(
    userApi.getUserWorkspaces(
      scope.userId,
      [scope.lifecycleStatus],
      scope.withStats,
    ),
  ).then((workspaces) =>
    (workspaces ?? []).map(
      (w) => augmentObjectWithIconAndImage(w) as Workspace,
    ),
  );

/**
 * The user's workspaces. The scope signal carries the stats view option, gated on
 * the view-options query having settled.
 *
 * **This is the only reactive observer of the key, on purpose.** Every other reader
 * goes through `UserWorkspacesService` (fetch-if-missing), so a workspace picker
 * opened over the home screen cannot refetch the list without stats and blank the
 * cards.
 */
export function injectUserWorkspacesQuery(
  scope: Signal<UserWorkspacesScope | null>,
) {
  const userApi = inject(UserApiService);

  return injectQuery(() => {
    const current = scope();

    return {
      queryKey: current
        ? userWorkspacesQueryKey(current)
        : (['user', null, 'workspaces'] as const),
      queryFn: () => fetchWorkspaces(userApi, current!),
      enabled: !!current,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/**
 * Imperative fetch-if-missing on the same key, the sanctioned tier for services.
 * `staleTime: Infinity` is what makes it fetch-if-missing: consumers serve from the
 * cache whenever it holds anything, and only request when it does not (or after an
 * explicit invalidation).
 */
export function injectFetchUserWorkspaces() {
  const queryClient = inject(QueryClient);
  const userApi = inject(UserApiService);

  return (scope: UserWorkspacesScope) =>
    queryClient.fetchQuery({
      queryKey: userWorkspacesQueryKey(scope),
      queryFn: () => fetchWorkspaces(userApi, scope),
      staleTime: Infinity,
      retry: false,
    });
}

/**
 * The three object collections of one workspace, as the workspace screen renders
 * them. The response also carries `workspaces` / `export_schedules` / `alerts`,
 * which nothing consumes, so the queryFn drops them.
 */
export interface WorkspaceObjects {
  feeds: Feed[];
  dashboards: Dashboard[];
  dimensions: Dimension[];
}

/**
 * Inputs of one workspace's object list. `lifecycleStatus` is the Active /
 * Archived / Deleted toolbar filter; `companyId` is in the key for the same reason
 * as in `UserWorkspacesScope`.
 */
export interface WorkspaceObjectsScope {
  workspaceId: string;
  companyId: string;
  lifecycleStatus: number;
}

/** Mirrors GET /user/workspace_objects. */
export const workspaceObjectsQueryKey = ({
  workspaceId,
  companyId,
  lifecycleStatus,
}: WorkspaceObjectsScope) =>
  [
    'user',
    'workspace_objects',
    { workspaceId, companyId, lifecycleStatus },
  ] as const;

/**
 * Every cached workspace-object list, whatever the workspace or lifecycle status -
 * what the cache patches sweep.
 */
export const workspaceObjectsQueriesFilter = (): QueryFilters => ({
  predicate: (query) => query.queryKey[1] === 'workspace_objects',
});

/** The cached object lists of **one** workspace, whatever the lifecycle status. */
export const workspaceObjectsQueriesFilterFor = (
  workspaceId: string,
): QueryFilters => ({
  predicate: (query) =>
    query.queryKey[1] === 'workspace_objects' &&
    (query.queryKey[2] as Partial<WorkspaceObjectsScope> | null)
      ?.workspaceId === workspaceId,
});

const mapAugment = <T extends Feed | Dashboard | Dimension>(
  objects?: T[],
): T[] =>
  (objects ?? []).map((object) => augmentObjectWithIconAndImage(object) as T);

const fetchWorkspaceObjects = (
  userApi: UserApiService,
  scope: WorkspaceObjectsScope,
): Promise<WorkspaceObjects> =>
  firstValueFrom(
    userApi.getUserWorkspaceObjects(scope.workspaceId, [scope.lifecycleStatus]),
  ).then((response) => ({
    feeds: mapAugment<Feed>(response?.feeds),
    dashboards: mapAugment<Dashboard>(response?.dashboards),
    dimensions: mapAugment<Dimension>(response?.dimensions),
  }));

/**
 * One workspace's feeds, dashboards and dimensions.
 *
 * **The workspace screen is the only reactive observer.** The widget sidebar's feed
 * picker goes through `WorkspaceObjectsService` (fetch-if-missing), so a dashboard
 * cannot refetch the list the workspace screen is rendering.
 */
export function injectWorkspaceObjectsQuery(
  scope: Signal<WorkspaceObjectsScope | null>,
) {
  const userApi = inject(UserApiService);

  return injectQuery(() => {
    const current = scope();

    return {
      queryKey: current
        ? workspaceObjectsQueryKey(current)
        : (['user', 'workspace_objects', null] as const),
      queryFn: () => fetchWorkspaceObjects(userApi, current!),
      enabled: !!current,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/** Imperative fetch-if-missing on the same key, for `WorkspaceObjectsService`. */
export function injectFetchWorkspaceObjects() {
  const queryClient = inject(QueryClient);
  const userApi = inject(UserApiService);

  return (scope: WorkspaceObjectsScope) =>
    queryClient.fetchQuery({
      queryKey: workspaceObjectsQueryKey(scope),
      queryFn: () => fetchWorkspaceObjects(userApi, scope),
      staleTime: Infinity,
      retry: false,
    });
}

/**
 * Create a workspace. The hook owns the cache append - the response row goes
 * straight into every cached workspace list, no refetch. A failed create is
 * silent by design.
 */
export function injectCreateWorkspaceMutation() {
  const workspaceApi = inject(WorkspaceApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      title,
      description,
      title_i18n,
    }: {
      title: string;
      description: string;
      title_i18n?: Record<string, string>;
    }) =>
      firstValueFrom(
        workspaceApi.createWorkspace(title, description, title_i18n),
      ),
    onSuccess: (workspace: Workspace) => {
      queryClient.setQueriesData<Workspace[]>(
        userWorkspacesQueriesFilter(),
        (workspaces) =>
          workspaces
            ? [
                ...workspaces,
                augmentObjectWithIconAndImage(workspace) as Workspace,
              ]
            : workspaces,
      );

      toast.showSuccess(
        translate.instant(
          'workspace-editor-popup.msg.workspace-created-successfully',
        ),
      );
    },
  }));
}

/**
 * Update a workspace. Same arrangement as the create hook: the response row
 * replaces its cached counterpart in place.
 */
export function injectUpdateWorkspaceMutation() {
  const workspaceApi = inject(WorkspaceApiService);
  const queryClient = inject(QueryClient);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      id,
      workspace,
    }: {
      id: string;
      workspace: Record<string, unknown>;
    }) => firstValueFrom(workspaceApi.updateWorkspace(id, workspace)),
    onSuccess: (updated: Workspace) => {
      queryClient.setQueriesData<Workspace[]>(
        userWorkspacesQueriesFilter(),
        (workspaces) =>
          workspaces?.map((workspace) =>
            workspace.id === updated.id
              ? (augmentObjectWithIconAndImage(updated) as Workspace)
              : workspace,
          ),
      );

      toast.showSuccess(
        translate.instant(
          'workspace-editor-popup.msg.workspace-updated-successfully',
        ),
      );
    },
  }));
}
